import { Injectable } from "@angular/core";
import { Validators } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { get } from "lodash";
import { environment } from "../../../../environments/environment";
import { AuthenticationService } from "../../../core/authentication/authentication.service";
import { LoadingService } from "../../../core/loader/loading.service";
import { DataTableConfig } from "../../../shared/components/data-table/data-table-service.type";
import { DataTableComponent } from "../../../shared/components/data-table/data-table.component";
import { DataTableService } from "../../../shared/components/data-table/data-table.service";
import {
  DataTable,
  DataTableColumn,
  DataTableParamsWhere,
  GetDataEvent,
  UpdateDataEvent,
} from "../../../shared/components/data-table/data-table.type";
import { DeleteModalComponent } from "../../../shared/components/delete-modal/delete-modal.component";
import { DemoDataModalComponent } from "../../../shared/components/demo-data-modal/demo-modal.component";
import { GenericModalComponent } from "../../../shared/components/generic-modal/generic-modal.component";
import { GenericModalData } from "../../../shared/components/generic-modal/generic-modal.type";
import { QuickViewModalComponent } from "../../../shared/components/quick-view-modal/quick-view-modal.component";
import { UserInfo } from "../../../shared/types/user-info";
import { formatPossession } from "../../../shared/utils/text-formatter";
import { CompanyTableService } from "../companies/company-table.service";
import { Company } from "../companies/company.type";
import { getPartnerSearchFilterOptions, partnerStatuses, partnerTypes } from "./partner";
import { PartnerService } from "./partner.service";
import { Partner } from "./partner.type";

@Injectable({
  providedIn: "root",
})
export class PartnerTableService {
  currentUser: UserInfo;

  constructor(
    private router: Router,
    private dialog: MatDialog,
    private loader: LoadingService,
    private authenticationService: AuthenticationService,
    private partnerService: PartnerService,
    private companyTableService: CompanyTableService,
    private dataTableService: DataTableService<Partner>,
  ) {
    this.currentUser = this.authenticationService.currentUserInfo;
  }

  /** Get table config */
  getPartnersTableConfig = (config?: DataTableConfig): DataTable<Partner> => {
    const { hidden, shown, actionColumn, inlineEdit, selection, syncWithQueryParams, entities } = config || {};

    const columns: DataTableColumn<Partner>[] = (
      [
        {
          prop: "name",
          name: "Name",
          link: {
            path: (partner) => `/partners/${partner.id}`,
          },
          inlineEdit: {
            control: "input",
            disabled: (partner) => inlineEdit?.disabled?.(partner),
            validators: () => [Validators.minLength(5), Validators.maxLength(200)],
          },
        },
        {
          prop: "partnerIdentifier",
          name: "Partner ID",
        },
        {
          prop: "type",
          name: "Type",
          render: (partner) => partnerTypes.find((type) => type.value === partner.type)?.label,
          inlineEdit: {
            control: "select",
            disabled: (partner) => inlineEdit?.disabled?.(partner),
            select: {
              options: [...partnerTypes],
            },
          },
        },
        {
          prop: "status",
          name: "Status",
          render: (partner) => partnerStatuses.find((status) => status.value === partner.status)?.label,
          inlineEdit: {
            control: "select",
            disabled: (partner) => inlineEdit?.disabled?.(partner),
            select: {
              options: [...partnerStatuses],
            },
          },
        },
      ] as DataTableColumn<Partner>[]
    )
      .filter((col) => (shown ? shown.includes(col.prop) : true) && (hidden ? !hidden.includes(col.prop) : true))
      .map((col) => ({
        ...col,
        inlineEdit: {
          ...col?.inlineEdit,
          /** Use the set condition and the new one passed here */
          disabled: (partner) => col?.inlineEdit?.disabled?.(partner),
        },
      }));

    return {
      id: "partners",
      entityName: "Partner",
      syncWithQueryParams: syncWithQueryParams ?? true,
      toolBar: {
        exportCsv: {
          filename: "partners",
          data: {
            browser: (queryParams) =>
              this.partnerService.getPartners({
                ...queryParams,
                where: { users: { id: entities?.user?.id }, ...queryParams.where },
                relations: { users: true },
              }),
            email: (queryParams) =>
              this.partnerService.getPartnersQueue({
                ...queryParams,
                where: { users: { id: entities?.user?.id }, ...queryParams.where },
                relations: { users: true },
              }),
          },
        },
      },
      columns,
      defaultColumns: [],
      selection: {
        ...selection,
        selectedRows: [],
      },
      actionColumn: actionColumn?.hidden
        ? null
        : [
            {
              icon: "zoom_in",
              text: "Quick View",
              function: (partner) => this.viewPartner(partner, config),
              hidden: actionColumn?.quickView?.hidden,
            },
            {
              icon: "table_rows",
              text: "View Companies",
              function: this.viewCompanies,
            },
            {
              icon: "visibility",
              text: "View",
              function: this.managePartner,
              hidden: () => actionColumn?.view?.hidden,
            },
            {
              icon: "clear",
              text: "Delete",
              function: this.deletePartner,
              hidden: () => actionColumn?.delete?.hidden || !this.currentUser.isGlobalAdmin(),
            },
            {
              icon: "create",
              text: "Create demo data",
              function: this.createDemoData,
              hidden: () => environment.production || !this.currentUser.isGlobalAdmin(),
            },
          ],
      searchBar: [
        {
          availableSearchFields: getPartnerSearchFilterOptions(this.currentUser, { hidden, shown }),
        },
      ],
    };
  };

  /** Populate table */
  getPartners = (event: GetDataEvent<Partner>): void => {
    const { onError, onResult, queryParams } = event;
    this.partnerService
      .getPartners({
        ...queryParams,
        where: {
          ...queryParams?.where,
        },
      })
      .subscribe(
        (data): void => {
          onResult(data);
        },
        (error): void => {
          onError(error);
        },
      );
  };

  getPartnersPost = (event: GetDataEvent<Partner>): void => {
    const { onError, onResult, queryParams } = event;
    this.partnerService
      .getPartnersPost({
        ...queryParams,
        where: {
          ...queryParams?.where,
        },
      })
      .subscribe(
        (data): void => {
          onResult(data);
        },
        (error): void => {
          onError(error);
        },
      );
  };

  /** Update table */
  update = (event: UpdateDataEvent<Partner>): void => {
    const { onError, onResult, row, updatedValues } = event;
    if (row.id && updatedValues) {
      this.loader.show();
      this.partnerService.updatePartner(row.id, { ...updatedValues }).subscribe(
        (data): void => {
          onResult(data);
          this.loader.hide();
        },
        (error): void => {
          onError(error);
          this.loader.hide();
        },
      );
    } else {
      onError();
    }
  };

  //#region Private Methods

  private viewPartner = (partner: Partner, config?: DataTableConfig): void => {
    const columns = this.getPartnersTableConfig(config).columns;

    const data = columns
      .filter((col) => !col.hidden)
      .map((col) => ({ name: col.name, value: this.dataTableService.parseData(partner, col, get(partner, col.prop)) }));

    this.dialog.open(QuickViewModalComponent, {
      width: "500px",
      data: {
        title: "Quick-view",
        data,
      },
      panelClass: "quick-view-modal",
    });
  };

  private viewCompanies = (partner: Partner): void => {
    this.dialog.open<GenericModalComponent, GenericModalData<{ config: DataTable<Company> }>>(GenericModalComponent, {
      width: "1400px",
      panelClass: "custom-dialog-container",
      data: {
        title: formatPossession(partner.name, "Companies"),
        buttons: { hide: true },
        component: {
          clazz: DataTableComponent,
          inputs: {
            config: this.companyTableService.getCompaniesTableConfig({
              entities: { partner },
              hidden: ["partner.name"],
              actionColumn: { hidden: true },
              inlineEdit: { disabled: () => true },
              syncWithQueryParams: false,
            }),
          },
          events: [
            {
              eventName: "getData",
              handler: (_dynamicForm: any, event: GetDataEvent<Company>) => {
                return this.companyTableService.getCompanies({
                  ...event,
                  queryParams: {
                    ...event.queryParams,
                    where: { ...event.queryParams.where, partnerId: partner.id } as DataTableParamsWhere<Company>,
                  },
                });
              },
            },
          ],
        },
      },
    });
  };

  private managePartner = (partner: Partner): void => {
    this.router.navigate([`/partners/${partner.id}`]);
  };

  private deletePartner = (partner: Partner, table: DataTableComponent<Partner>): void => {
    const dialogRef = this.dialog.open(DeleteModalComponent, {
      width: "300px",
      panelClass: "custom-dialog-container",
      disableClose: true,
      data: {
        name: "partner",
        data: partner,
        url: this.partnerService.basePath,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        table.search({ isDeletingOrUnlinking: true });
      }
    });
  };

  private createDemoData = (partner: Partner, table: DataTableComponent<Partner>): void => {
    const dialogRef = this.dialog.open(DemoDataModalComponent, {
      width: "450px",
      panelClass: "custom-dialog-container",
      disableClose: true,
      data: {
        information: [
          "3 companies",
          "3 locations per company",
          "4 contracts per company",
          "20 assets per company",
          "4 linked assets per contract",
          "1 ticket with a comment per active contract",
        ],
        name: partner.name,
        partner: partner,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        table.search({ isDeletingOrUnlinking: true });
      }
    });

    //#endregion
  };
}
